// src/components/home/VibeCTASection.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Shield, Clock, Star, Brain, Heart, Crown } from 'lucide-react';
import Button from '../shared/Button';
import { useApp } from '../../context/AppContext';

const VibeCTASection = () => {
  const { setCurrentView } = useApp();
  
  return (
    <section className="py-20 bg-gradient-to-br from-purple-900 via-indigo-900 to-purple-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto space-y-8"
        >
          {/* Floating Elements */}
          <div className="relative">
            <motion.div
              animate={{ y: [-5, 5, -5], rotate: [0, 5, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -top-8 -left-8 text-6xl"
            >
              ✨
            </motion.div>
            <motion.div
              animate={{ y: [5, -5, 5], rotate: [0, -5, 5, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute -top-4 -right-8 text-7xl"
            >
              🚀
            </motion.div>
          </div>
          
          {/* Header */}
          <motion.div
            initial={{ scale: 0.9 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Crown className="w-6 h-6 text-yellow-400" />
              <span className="px-3 py-1 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-full text-yellow-300 font-medium">
                Your Time Is Now
              </span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight">
              Ready to Level Up Your
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400">
                Inner Voice Game?
              </span>
            </h2>
            
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Get ahead of the game and be one of the first to transform their self-talk and discover what confident thinking actually feels like. 
              <span className="text-purple-300 font-semibold"> Your future self is waiting.</span>
            </p>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button 
              size="lg" 
              className="min-w-[250px] bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-2xl text-lg font-bold relative overflow-hidden group"
              onClick={() => setCurrentView('vibe-assessment-intro')}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity"
                whileHover={{ scale: 1.1 }}
              />
              <span className="relative flex items-center">
                <Sparkles className="w-5 h-5 mr-2" />
                Start Your Vibe Check
              </span>
            </Button>
            
            <Button 
              variant="secondary" 
              size="lg"
              className="min-w-[200px] text-lg border-2 border-purple-400/50 hover:border-purple-400"
              onClick={() => setCurrentView('modules')}
            >
              Explore Modules
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="space-y-6"
          >
            <div className="flex flex-wrap justify-center gap-8 text-sm">
              {[
                { icon: Shield, text: '100% Private & Secure', color: 'text-green-400' },
                { icon: Clock, text: '5-12 min per session', color: 'text-blue-400' },
                { icon: Star, text: 'Science-backed content', color: 'text-yellow-400' },
                { icon: Brain, text: 'Built for specifically for young adults', color: 'text-purple-400' },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                  <span className="text-slate-300 font-medium">{item.text}</span>
                </motion.div>
              ))}
            </div>
            
            <p className="text-slate-400 text-sm">
              Free to start • No email required • Data stays on your device • Start transformation in 2 minutes
            </p>
          </motion.div>

          {/* Social Proof */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 1 }}
            className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-6 max-w-3xl mx-auto"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Heart className="w-5 h-5 text-pink-400" />
              <span className="text-pink-300 font-medium">Real Talk From Real Users</span>
            </div>
            <blockquote className="text-lg text-white mb-4 italic">
              "Finally, something that gets how my brain actually works. No weird therapy speak, just real techniques that helped me stop being so hard on myself."
            </blockquote>
            <div className="text-slate-400 text-sm">
              — Abbey, 18 • Thrilled to be transforming her inner critic
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default VibeCTASection;